<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAppConfig } from '@/composables/useAppConfig'
import { useLocale } from '@/composables/useLocale'
import { useLandingPageLinks } from '@/composables/useLandingPageLinks'
import LanguageSwitcher from '@/components/ui/LanguageSwitcher.vue'
import ThemeToggle from '@/components/ui/ThemeToggle.vue'
import AppLogo from '@/components/ui/AppLogo.vue'

const router = useRouter()
const { config } = useAppConfig()
const { t } = useLocale()

const currentYear = new Date().getFullYear()

const { isLandingEnabled, landingUrl } = useLandingPageLinks()

const currentRoute = computed(() => router.currentRoute.value.name)
const pageTitle = computed(() => {
  switch (currentRoute.value) {
    case 'auth-login':
      return t('auth.login.title')
    case 'auth-register':
      return t('auth.register.title')
    case 'auth-forgot-password':
      return t('auth.forgotPassword.title')
    case 'auth-reset-password':
      return t('auth.resetPassword.title')
    case 'auth-verify-email':
      return t('auth.verifyEmail.title')
    default:
      return t('auth.pages.authentication')
  }
})
</script>

<template>
  <div
    class="tw-min-h-screen tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-surface-50 tw-to-surface-100 tw-py-12 tw-px-4 sm:tw-px-6 lg:tw-px-8"
  >
    <div class="tw-w-full tw-max-w-md">
      <!-- Logo with landing link (when enabled) -->
      <a
        v-if="isLandingEnabled && landingUrl"
        :href="landingUrl"
        class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-6 tw-group"
        data-testid="auth-logo-link"
      >
        <AppLogo size="lg" />
        <span class="tw-text-xl tw-font-bold tw-text-surface-900 group-hover:tw-text-primary-600 dark:group-hover:tw-text-primary-400 tw-transition-colors">
          {{ config.name }}
        </span>
      </a>
      <!-- Logo without link (when landing disabled) -->
      <div
        v-else
        class="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-mb-6"
        data-testid="auth-logo"
      >
        <AppLogo size="lg" />
        <span class="tw-text-xl tw-font-bold tw-text-surface-900">
          {{ config.name }}
        </span>
      </div>

      <!-- Auth Form Container -->
      <div
        class="tw-bg-surface-0 tw-shadow-lg tw-rounded-2xl tw-p-8 tw-border tw-border-surface-200"
      >
        <!-- Header with title and controls -->
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-6">
          <h1 class="tw-text-xl tw-font-bold tw-text-surface-900">
            {{ pageTitle }}
          </h1>
          <div class="tw-flex tw-items-center tw-gap-2">
            <ThemeToggle size="sm" />
            <LanguageSwitcher :show-label="false" />
          </div>
        </div>

        <slot />
      </div>

      <!-- Footer -->
      <div class="tw-mt-8 tw-text-center">
        <p class="tw-text-sm tw-text-surface-500">
          &copy; {{ currentYear }}
          <a
            :href="config.copyrightOwnerUrl"
            class="tw-text-surface-500 hover:tw-text-surface-700 tw-transition-colors"
          >{{ config.copyrightOwner }}</a>. {{ t('common.footer.allRightsReserved') }}
        </p>
      </div>
    </div>
  </div>
</template>
